export const TENANT_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "archived", label: "Archived" },
];

export const TENANTS_LIST_VIEWS = {
  LIST: "list",
  GEOGRAPHY: "geography",
};

export const TENANTS_LIST_VIEW_OPTIONS = [
  { id: TENANTS_LIST_VIEWS.LIST, label: "Directory" },
  { id: TENANTS_LIST_VIEWS.GEOGRAPHY, label: "Geography" },
];

export const CARE_SECTOR_OPTIONS = [
  { value: "ndis", label: "NDIS" },
  { value: "aged_care", label: "Aged Care" },
  { value: "both", label: "Both" },
];

export const DEFAULT_TENANT_FILTERS = {
  search: "",
  status: ["active"],
  onTrial: false,
  readOnly: false,
  wildcard: false,
  billingLeftoverAr: false,
  careSector: "",
  stateCode: "",
  suburb: "",
  postcode: "",
  page: 1,
  perPage: 10,
  sortField: "name",
  sortOrder: "asc",
};

export const TENANT_WIDGET_PRESETS = {
  all: {
    status: [],
    onTrial: false,
    readOnly: false,
    wildcard: false,
    billingLeftoverAr: false,
    careSector: "",
    page: 1,
  },
  active: {
    status: ["active"],
    onTrial: false,
    readOnly: false,
    wildcard: false,
    billingLeftoverAr: false,
    careSector: "",
    page: 1,
  },
  suspended: {
    status: ["suspended"],
    onTrial: false,
    readOnly: false,
    wildcard: false,
    billingLeftoverAr: false,
    careSector: "",
    page: 1,
  },
  trial: {
    status: [],
    onTrial: true,
    readOnly: false,
    wildcard: false,
    billingLeftoverAr: false,
    careSector: "",
    page: 1,
  },
  readOnly: {
    status: [],
    onTrial: false,
    readOnly: true,
    wildcard: false,
    billingLeftoverAr: false,
    careSector: "",
    page: 1,
  },
  leftoverAr: {
    status: [],
    onTrial: false,
    readOnly: false,
    wildcard: false,
    billingLeftoverAr: true,
    careSector: "",
    page: 1,
  },
};

const normalizeStatusList = (status) => [...new Set(status || [])].sort();

export const filtersMatchTenantWidget = (filters, widgetKey) => {
  const preset = TENANT_WIDGET_PRESETS[widgetKey];
  if (!preset) {
    return false;
  }

  return (
    !filters.search &&
    normalizeStatusList(filters.status).join(",") ===
      normalizeStatusList(preset.status).join(",") &&
    Boolean(filters.onTrial) === Boolean(preset.onTrial) &&
    Boolean(filters.readOnly) === Boolean(preset.readOnly) &&
    Boolean(filters.wildcard) === Boolean(preset.wildcard) &&
    Boolean(filters.billingLeftoverAr) === Boolean(preset.billingLeftoverAr) &&
    (filters.careSector || "") === (preset.careSector || "")
  );
};

export const getActiveTenantWidget = (filters) => {
  const match = Object.keys(TENANT_WIDGET_PRESETS).find((key) =>
    filtersMatchTenantWidget(filters, key)
  );

  return match || null;
};

export const applyTenantWidgetPreset = (filters, widgetKey) => ({
  ...filters,
  ...(TENANT_WIDGET_PRESETS[widgetKey] || {}),
});

export const isDefaultTenantFilters = (filters) =>
  !filters.search &&
  !filters.stateCode &&
  !filters.suburb &&
  !filters.postcode &&
  filtersMatchTenantWidget(filters, "active") &&
  filters.page === 1 &&
  filters.sortField === DEFAULT_TENANT_FILTERS.sortField &&
  filters.sortOrder === DEFAULT_TENANT_FILTERS.sortOrder;

export const hasActiveTenantFilters = (filters) => !isDefaultTenantFilters(filters);

export const buildTenantsListQuery = (filters, basePath = "super-admin/tenants") => {
  const params = new URLSearchParams();

  if (filters.search) {
    params.append("search", filters.search);
  }

  (filters.status || []).forEach((status) => {
    params.append("status[]", status);
  });

  if (filters.onTrial) {
    params.append("on_trial", "1");
  }

  if (filters.readOnly) {
    params.append("read_only", "1");
  }

  if (filters.wildcard) {
    params.append("wildcard", "1");
  }

  if (filters.billingLeftoverAr) {
    params.append("billing_leftover_ar", "1");
  }

  if (filters.careSector) {
    params.append("care_sector", filters.careSector);
  }

  if (filters.stateCode) {
    params.append("state_code", filters.stateCode);
  }

  if (filters.suburb) {
    params.append("suburb", filters.suburb);
  }

  if (filters.postcode) {
    params.append("postcode", filters.postcode);
  }

  params.append("page", String(filters.page || 1));
  params.append("per_page", String(filters.perPage || 10));
  params.append("sort_field", filters.sortField || "created_at");
  params.append("sort_order", filters.sortOrder || "desc");

  const query = params.toString();
  return query ? `${basePath}?${query}` : basePath;
};

export const formatCareSectorLabel = (careSector) => {
  const match = CARE_SECTOR_OPTIONS.find((option) => option.value === careSector);
  return match?.label || "—";
};

export const getTenantStatusBadge = (status) => {
  switch (status) {
    case "active":
      return { label: "Active", variant: "success" };
    case "suspended":
      return { label: "Suspended", variant: "danger" };
    case "archived":
      return { label: "Archived", variant: "neutral" };
    default:
      return { label: status || "Unknown", variant: "neutral" };
  }
};

export const toggleFilterValue = (currentValues, value) => {
  if (currentValues.includes(value)) {
    return currentValues.filter((item) => item !== value);
  }

  return [...currentValues, value];
};

const appendArrayParam = (params, key, values) => {
  (values || []).forEach((value) => {
    if (value) {
      params.append(`${key}[]`, value);
    }
  });
};

export const buildTenantsListSearchParams = (filters, { view = null } = {}) => {
  const params = new URLSearchParams();

  if (filters.search) {
    params.set("search", filters.search);
  }

  if (filters.status.length === 0) {
    params.set("status", "all");
  } else if (
    filters.status.length !== 1 ||
    filters.status[0] !== DEFAULT_TENANT_FILTERS.status[0]
  ) {
    appendArrayParam(params, "status", filters.status);
  }

  if (filters.onTrial) {
    params.set("on_trial", "1");
  }

  if (filters.readOnly) {
    params.set("read_only", "1");
  }

  if (filters.wildcard) {
    params.set("wildcard", "1");
  }

  if (filters.billingLeftoverAr) {
    params.set("billing_leftover_ar", "1");
  }

  if (filters.careSector) {
    params.set("care_sector", filters.careSector);
  }

  if (filters.stateCode) {
    params.set("state", filters.stateCode);
  }

  if (filters.suburb) {
    params.set("suburb", filters.suburb);
  }

  if (filters.postcode) {
    params.set("postcode", filters.postcode);
  }

  if (filters.page && filters.page !== 1) {
    params.set("page", String(filters.page));
  }

  if (filters.sortField && filters.sortField !== DEFAULT_TENANT_FILTERS.sortField) {
    params.set("sort_field", filters.sortField);
  }

  if (filters.sortOrder && filters.sortOrder !== DEFAULT_TENANT_FILTERS.sortOrder) {
    params.set("sort_order", filters.sortOrder);
  }

  if (view === TENANTS_LIST_VIEWS.GEOGRAPHY) {
    params.set("view", TENANTS_LIST_VIEWS.GEOGRAPHY);
  }

  return params;
};

export const serializeTenantsListSearchParams = (searchParams) => {
  const params = new URLSearchParams(searchParams);
  params.delete("focus");
  return params.toString();
};

export const tenantFiltersEqual = (left, right) => {
  if (!left || !right) {
    return false;
  }

  return (
    left.search === right.search &&
    normalizeStatusList(left.status).join(",") === normalizeStatusList(right.status).join(",") &&
    Boolean(left.onTrial) === Boolean(right.onTrial) &&
    Boolean(left.readOnly) === Boolean(right.readOnly) &&
    Boolean(left.wildcard) === Boolean(right.wildcard) &&
    Boolean(left.billingLeftoverAr) === Boolean(right.billingLeftoverAr) &&
    (left.careSector || "") === (right.careSector || "") &&
    (left.stateCode || "") === (right.stateCode || "") &&
    (left.suburb || "") === (right.suburb || "") &&
    (left.postcode || "") === (right.postcode || "") &&
    Number(left.page || 1) === Number(right.page || 1) &&
    (left.sortField || DEFAULT_TENANT_FILTERS.sortField) ===
      (right.sortField || DEFAULT_TENANT_FILTERS.sortField) &&
    (left.sortOrder || DEFAULT_TENANT_FILTERS.sortOrder) ===
      (right.sortOrder || DEFAULT_TENANT_FILTERS.sortOrder)
  );
};

export const parseTenantsListView = (searchParams) =>
  searchParams.get("view") === TENANTS_LIST_VIEWS.GEOGRAPHY
    ? TENANTS_LIST_VIEWS.GEOGRAPHY
    : TENANTS_LIST_VIEWS.LIST;

export const getTenantsListPageSubtitle = (view, filters, total = null) => {
  if (view === TENANTS_LIST_VIEWS.GEOGRAPHY) {
    return "Tenant and revenue distribution across Australian states";
  }

  return getTenantsListSubtitle(filters, total);
};

export const parseTenantFiltersFromSearchParams = (searchParams) => {
  const status = [
    ...searchParams.getAll("status[]"),
    ...searchParams.getAll("status"),
  ].filter(Boolean);

  const page = Number(searchParams.get("page"));
  const perPage = Number(searchParams.get("per_page"));

  let resolvedStatus = DEFAULT_TENANT_FILTERS.status;
  if (searchParams.get("status") === "all") {
    resolvedStatus = [];
  } else if (status.length > 0) {
    resolvedStatus = [...new Set(status)];
  }

  return {
    ...DEFAULT_TENANT_FILTERS,
    search: searchParams.get("search") || "",
    status: resolvedStatus,
    onTrial: searchParams.get("on_trial") === "1",
    readOnly: searchParams.get("read_only") === "1",
    wildcard: searchParams.get("wildcard") === "1",
    billingLeftoverAr: searchParams.get("billing_leftover_ar") === "1",
    careSector: searchParams.get("care_sector") || "",
    stateCode: (searchParams.get("state") || searchParams.get("state_code") || "").toUpperCase(),
    suburb: (searchParams.get("suburb") || "").toUpperCase(),
    postcode: searchParams.get("postcode") || "",
    page: Number.isFinite(page) && page > 0 ? page : 1,
    perPage: Number.isFinite(perPage) && perPage > 0 ? perPage : DEFAULT_TENANT_FILTERS.perPage,
    sortField: searchParams.get("sort_field") || DEFAULT_TENANT_FILTERS.sortField,
    sortOrder: searchParams.get("sort_order") || DEFAULT_TENANT_FILTERS.sortOrder,
  };
};

export const getTenantsListSubtitle = (filters, total = null) => {
  const widget = getActiveTenantWidget(filters);
  const parts = [];

  if (widget === "active") {
    parts.push("Active tenants");
  } else if (widget === "all") {
    parts.push("All tenants");
  } else if (widget === "suspended") {
    parts.push("Suspended tenants");
  } else if (widget === "trial") {
    parts.push("Tenants on trial");
  } else if (widget === "readOnly") {
    parts.push("Read-only tenants");
  } else if (widget === "leftoverAr") {
    parts.push("Active tenants with leftover AR");
  } else if (filters.status.length) {
    parts.push(`Status: ${filters.status.join(", ")}`);
  } else {
    parts.push("Filtered tenants");
  }

  if (filters.search) {
    parts.push(`matching “${filters.search}”`);
  }

  if (filters.stateCode) {
    parts.push(`in ${filters.stateCode}`);
  }

  if (filters.suburb) {
    parts.push(filters.suburb);
  }

  if (filters.postcode) {
    parts.push(filters.postcode);
  }

  if (Number.isFinite(total)) {
    parts.push(`${total} total`);
  }

  return parts.join(" · ");
};

export const buildTenantsListPath = (filters, { focus = null } = {}) => {
  const params = buildTenantsListSearchParams(filters);

  if (focus) {
    params.set("focus", focus);
  }

  const query = params.toString();

  return query ? `/tenants?${query}` : focus ? `/tenants?focus=${focus}` : "/tenants";
};
